/** @jsxImportSource @emotion/react */
import { useProjectDataContext } from "pages/project/ProjectDataProvider";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { MarkdownEditor } from "components/MarkdownEditor/MarkdownEditor";
import { captureException } from "@sentry/react";
import { updateCacheFragment } from "utils/cache-fragments";
import { libraryProjectPatch } from "hooks/useLibraryProjectPatchDjango";
import { useState } from "react";
import { EuiPanel } from "@inscopix/ideas-eui";
import { isNonNullish } from "utils/isNonNullish";
import { css } from "@emotion/react";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";
import { ButtonEmptyPermissioned } from "components/ButtonEmptyPermissioned/ButtonEmptyPermissioned";

export const PanelDescription = () => {
  const { project } = useProjectDataContext();
  const [edit, setEdit] = useState(false);

  const hasEditPermission = useProjectPermissionStore(
    (s) => s.permissions["edit"],
  );

  const onSave = async (markdown: string) => {
    try {
      const projectUpdated = await libraryProjectPatch(project.id, {
        description: markdown,
      });

      updateCacheFragment({
        __typename: "Project",
        id: project.id,
        update: (data) => ({ ...data, ...projectUpdated }),
      });

      addUtilityToastSuccess("Successfully updated project description");
    } catch (err) {
      captureException(err);
      addUtilityToastFailure("Failed to update project description");
    }
  };

  const editor = (
    <MarkdownEditor
      edit={edit}
      setEdit={setEdit}
      initialValue={project.description ?? ""}
      emptyPrompt="Add markdown documentation here describing this project."
      onSave={onSave}
      height={600}
    />
  );

  if (!edit) {
    return (
      <EuiPanel
        paddingSize="l"
        hasShadow={false}
        hasBorder={true}
        css={css`
          position: relative;
        `}
      >
        {hasEditPermission &&
          isNonNullish(project.description) &&
          project.description !== "" && (
            <div
              css={css`
                position: absolute;
                top: 4px;
                right: 4px;
              `}
            >
              <ButtonEmptyPermissioned
                aria-label={"Edit project documentation"}
                data-test-subj="project-description-edit-button"
                iconType="documentEdit"
                size="s"
                onClick={() => setEdit(true)}
                requiredPermission="edit"
              >
                Edit
              </ButtonEmptyPermissioned>
            </div>
          )}
        {editor}
      </EuiPanel>
    );
  }

  return editor;
};
